'use client';

import { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { Link } from '@/i18n/navigation';
import { getMemberColor, type Album } from '@/lib/albums-types';
import FilterSection from '@/components/FilterSection';

type ViewMode = 'showcase' | 'list';

// 成員篩選
const MEMBER_FILTERS = [
  { slug: 'all', value: '', color: undefined },
  { slug: 'group', value: 'group', color: '#D4AF37' },
  { slug: 'GD', value: 'GD', color: '#ED609F' },
  { slug: 'Taeyang', value: 'Taeyang', color: '#D7171E' },
  { slug: 'TOP', value: 'TOP', color: '#8FC126' },
  { slug: 'Daesung', value: 'Daesung', color: '#F4E727' },
];

// 專輯類型篩選
const TYPE_FILTERS = [
  { slug: 'all', value: '' },
  { slug: 'regular', value: '正規' },
  { slug: 'mini', value: '迷你' },
  { slug: 'single', value: '單曲' },
  { slug: 'special', value: '特別' },
];

interface Filters {
  members: string[];
  years: number[];
  types: string[];
}

function filterAlbums(albums: Album[], filters: Filters): Album[] {
  return albums.filter((album) => {
    // 成員篩選
    if (filters.members.length > 0) {
      const memberMatch = filters.members.some((m) => {
        if (m === 'group') return album.artistType === 'group' || album.artistType === 'unit';
        return album.member === m;
      });
      if (!memberMatch) return false;
    }

    // 年份篩選
    if (filters.years.length > 0) {
      if (!filters.years.includes(album.year)) return false;
    }

    // 類型篩選
    if (filters.types.length > 0) {
      const typeMatch = filters.types.some((t) => album.albumType.includes(t));
      if (!typeMatch) return false;
    }

    return true;
  });
}

interface AlbumsClientProps {
  albums: Album[];
}

export default function AlbumsClient({ albums }: AlbumsClientProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('showcase');
  const [filters, setFilters] = useState<Filters>({ members: [], years: [], types: [] });
  const [selectedAlbumIndex, setSelectedAlbumIndex] = useState(0);
  const t = useTranslations('albums');

  // 取得所有年份選項
  const yearOptions = useMemo(() => {
    const years = [...new Set(albums.map((a) => a.year))].sort((a, b) => b - a);
    return years;
  }, [albums]);

  // 列表模式才需要篩選，輪播模式顯示全部
  const displayAlbums = viewMode === 'list' ? filterAlbums(albums, filters) : albums;
  const selectedAlbum = displayAlbums[selectedAlbumIndex] || displayAlbums[0];

  // Reset selection when filter changes
  useEffect(() => {
    setSelectedAlbumIndex(0);
  }, [filters]);

  // Reset selection when switching view mode
  useEffect(() => {
    setSelectedAlbumIndex(0);
  }, [viewMode]);

  return (
    <div className="bg-[#0A0A0A] min-h-screen">
      {/* Header */}
      <div className="px-4 md:px-20 pt-8 md:pt-12 pb-4 md:pb-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-[40px] font-extrabold tracking-[2px] text-[#F5F5F0] font-en mb-1 md:mb-2">
              {t('title')}
            </h1>
            <p className="text-xs md:text-sm text-[#888888]">
              {t('subtitle')}
            </p>
          </div>

          {/* View Mode Switcher */}
          <div className="flex items-center gap-1 bg-[#1A1A1A] rounded-lg p-1">
            <button
              onClick={() => setViewMode('showcase')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                viewMode === 'showcase'
                  ? 'bg-[#D4AF37] text-[#0A0A0A]'
                  : 'text-[#888888] hover:text-[#F5F5F0]'
              }`}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
              </svg>
              <span className="hidden md:inline">{t('carousel')}</span>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                viewMode === 'list'
                  ? 'bg-[#D4AF37] text-[#0A0A0A]'
                  : 'text-[#888888] hover:text-[#F5F5F0]'
              }`}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
              <span className="hidden md:inline">{t('list')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      {viewMode === 'showcase' ? (
        <ShowcaseView
          albums={displayAlbums}
          selectedAlbum={selectedAlbum}
          selectedIndex={selectedAlbumIndex}
          onSelectAlbum={setSelectedAlbumIndex}
        />
      ) : (
        <ListView
          albums={displayAlbums}
          filters={filters}
          yearOptions={yearOptions}
          onFiltersChange={setFilters}
          onSelectAlbum={(index) => {
            setSelectedAlbumIndex(index);
            setViewMode('showcase');
          }}
        />
      )}

      {/* Empty State */}
      {displayAlbums.length === 0 && (
        <div className="flex items-center justify-center py-20">
          <p className="text-[#888888]">{t('noAlbums')}</p>
        </div>
      )}
    </div>
  );
}

interface ShowcaseViewProps {
  albums: Album[];
  selectedAlbum: Album;
  selectedIndex: number;
  onSelectAlbum: (index: number) => void;
}

function ShowcaseView({ albums, selectedAlbum, selectedIndex, onSelectAlbum }: ShowcaseViewProps) {
  const t = useTranslations('albums');
  const memberColor = getMemberColor(selectedAlbum.member);
  const carouselRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [hasDragged, setHasDragged] = useState(false);

  // Smooth scroll to selected album
  useEffect(() => {
    if (carouselRef.current && !isDragging) {
      const container = carouselRef.current;
      const activeItem = container.children[selectedIndex] as HTMLElement;
      if (activeItem) {
        const containerWidth = container.offsetWidth;
        const itemLeft = activeItem.offsetLeft;
        const itemWidth = activeItem.offsetWidth;
        const scrollTo = itemLeft - containerWidth / 2 + itemWidth / 2;
        container.scrollTo({ left: scrollTo, behavior: 'smooth' });
      }
    }
  }, [selectedIndex, isDragging]);

  // Drag to scroll handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!carouselRef.current) return;
    setIsDragging(true);
    setHasDragged(false);
    setStartX(e.pageX - carouselRef.current.offsetLeft);
    setScrollLeft(carouselRef.current.scrollLeft);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !carouselRef.current) return;
    e.preventDefault();
    const x = e.pageX - carouselRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    if (Math.abs(walk) > 5) {
      setHasDragged(true);
    }
    carouselRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  const handleAlbumClick = (index: number) => {
    if (!hasDragged) {
      onSelectAlbum(index);
    }
  };

  if (!selectedAlbum) return null;

  return (
    <div className="flex flex-col">
      {/* Content */}
      <div className="flex flex-col items-center px-4 md:px-20 py-4 md:py-8 gap-6 md:gap-8">
        {/* Album Cover - Clickable */}
        <Link
          href={`/albums/${selectedAlbum.slug}`}
          className="w-[280px] h-[280px] md:w-[480px] md:h-[480px] rounded-xl overflow-hidden cursor-pointer group"
          style={{
            boxShadow: '0 12px 48px rgba(0,0,0,0.6), 0 4px 12px rgba(212,175,55,0.08)',
          }}
        >
          <div
            className="w-full h-full bg-[#1A1A1A] bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
            style={{ backgroundImage: `url(${selectedAlbum.coverUrl})` }}
          />
        </Link>

        {/* Album Info */}
        <div className="flex flex-col items-center gap-1 md:gap-2">
          <span
            className="text-xs md:text-sm tracking-[2px] font-en"
            style={{ color: memberColor }}
          >
            {selectedAlbum.year}
          </span>
          <h1 className="text-2xl md:text-[42px] font-bold text-[#F5F5F0]">
            {selectedAlbum.title}
          </h1>
          <p className="text-sm md:text-base text-[#888888]">
            {selectedAlbum.artist} · {selectedAlbum.albumType}
          </p>
        </div>
      </div>

      {/* Album Carousel */}
      <div className="w-full overflow-hidden py-6 md:py-8 flex flex-col items-center gap-3 md:gap-4 bg-[#0A0A0A]">
        <span className="text-[11px] md:text-[13px] font-medium tracking-[2px] text-[#666666]">
          {t('allAlbums')}
        </span>

        {/* Thumbnails */}
        <div
          ref={carouselRef}
          className={`w-full flex items-center gap-3 md:gap-4 overflow-x-auto scroll-smooth scrollbar-hide ${
            isDragging ? 'cursor-grabbing' : 'cursor-grab'
          }`}
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            paddingLeft: 'max(1rem, calc(50vw - 40px))',
            paddingRight: 'max(1rem, calc(50vw - 40px))',
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
        >
          {albums.map((album, index) => {
            const isActive = index === selectedIndex;
            const albumColor = getMemberColor(album.member);

            return (
              <button
                key={album.id}
                onClick={() => handleAlbumClick(index)}
                className="flex-shrink-0 flex flex-col items-center gap-1.5 md:gap-2 transition-all duration-300 select-none"
              >
                <div
                  className={`rounded-lg overflow-hidden transition-all duration-300 ${
                    isActive ? 'w-[80px] h-[80px] md:w-[110px] md:h-[110px] scale-100' : 'w-14 h-14 md:w-20 md:h-20 opacity-50 hover:opacity-75'
                  }`}
                  style={{
                    boxShadow: isActive ? `0 4px 16px ${albumColor}30` : 'none',
                    border: isActive ? `2px solid ${albumColor}` : 'none',
                  }}
                >
                  <div
                    className="w-full h-full bg-[#1A1A1A] bg-cover bg-center transition-transform duration-300 hover:scale-105"
                    style={{ backgroundImage: `url(${album.coverUrl})` }}
                  />
                </div>
                {isActive && (
                  <span
                    className="text-[10px] md:text-[11px] font-semibold tracking-[1px] transition-opacity duration-300"
                    style={{ color: albumColor }}
                  >
                    {album.title}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Dot indicators - hidden */}
        {/* <div className="flex items-center justify-center gap-1 md:gap-1.5">
          {albums.map((_, index) => (
            <button
              key={index}
              onClick={() => onSelectAlbum(index)}
              className={`rounded-full transition-all ${
                index === selectedIndex
                  ? 'w-1.5 h-1.5 md:w-[7px] md:h-[7px] bg-[#D4AF37]'
                  : 'w-1 h-1 md:w-[5px] md:h-[5px] bg-[#333333]'
              }`}
            />
          ))}
        </div> */}
      </div>
    </div>
  );
}

interface ListViewProps {
  albums: Album[];
  filters: Filters;
  yearOptions: number[];
  onFiltersChange: (filters: Filters) => void;
  onSelectAlbum: (index: number) => void;
}

// 多選篩選按鈕元件
function FilterChip({
  label,
  isActive,
  color,
  onClick,
}: {
  label: string;
  isActive: boolean;
  color?: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-2.5 md:px-4 py-1.5 md:py-2 rounded-full text-[10px] md:text-xs font-medium transition-all flex-shrink-0 ${
        isActive
          ? 'text-[#0A0A0A]'
          : color
            ? 'bg-transparent border'
            : 'bg-transparent border border-white/15 text-[#888888] hover:text-[#F5F5F0]'
      }`}
      style={
        isActive
          ? { backgroundColor: color || '#D4AF37' }
          : color
            ? { borderColor: `${color}40`, color }
            : undefined
      }
    >
      {label}
    </button>
  );
}

function ListView({ albums, filters, yearOptions, onFiltersChange, onSelectAlbum }: ListViewProps) {
  const t = useTranslations();
  const tFilters = useTranslations('filters');
  const tMembers = useTranslations('members');
  const tAlbumTypes = useTranslations('albumTypes');

  const toggleMember = (value: string) => {
    if (!value) {
      onFiltersChange({ ...filters, members: [] });
    } else {
      const newMembers = filters.members.includes(value)
        ? filters.members.filter((m) => m !== value)
        : [...filters.members, value];
      onFiltersChange({ ...filters, members: newMembers });
    }
  };

  const toggleYear = (year: number) => {
    const newYears = filters.years.includes(year)
      ? filters.years.filter((y) => y !== year)
      : [...filters.years, year];
    onFiltersChange({ ...filters, years: newYears });
  };

  const toggleType = (value: string) => {
    if (!value) {
      onFiltersChange({ ...filters, types: [] });
    } else {
      const newTypes = filters.types.includes(value)
        ? filters.types.filter((t) => t !== value)
        : [...filters.types, value];
      onFiltersChange({ ...filters, types: newTypes });
    }
  };

  const activeFilterCount = filters.members.length + filters.years.length + filters.types.length;

  return (
    <div className="px-4 md:px-20 pb-8 md:pb-12">

      {/* Filter Sections */}
      <FilterSection className="mb-6 md:mb-10" activeFilterCount={activeFilterCount} stickyPadding="px-4 md:px-20">
        <div className="space-y-3 md:space-y-4">
          {/* Member Filter */}
          <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
            <span className="text-[10px] md:text-xs text-[#666666] flex-shrink-0 w-12">{tFilters('member')}</span>
            <div className="flex items-center gap-1.5 md:gap-2 overflow-x-auto scrollbar-hide pb-1">
              {MEMBER_FILTERS.map((f) => (
                <FilterChip
                  key={f.value}
                  label={tMembers(f.slug)}
                  isActive={f.value === '' ? filters.members.length === 0 : filters.members.includes(f.value)}
                  color={f.color}
                  onClick={() => toggleMember(f.value)}
                />
              ))}
            </div>
          </div>

          {/* Year Filter */}
          <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
            <span className="text-[10px] md:text-xs text-[#666666] flex-shrink-0 w-12">{tFilters('year')}</span>
            <div className="flex items-center gap-1.5 md:gap-2 overflow-x-auto scrollbar-hide pb-1">
              <FilterChip
                label={t('common.all')}
                isActive={filters.years.length === 0}
                onClick={() => onFiltersChange({ ...filters, years: [] })}
              />
              {yearOptions.map((year) => (
                <FilterChip
                  key={year}
                  label={String(year)}
                  isActive={filters.years.includes(year)}
                  onClick={() => toggleYear(year)}
                />
              ))}
            </div>
          </div>

          {/* Type Filter */}
          <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
            <span className="text-[10px] md:text-xs text-[#666666] flex-shrink-0 w-12">{tFilters('type')}</span>
            <div className="flex items-center gap-1.5 md:gap-2 overflow-x-auto scrollbar-hide pb-1">
              {TYPE_FILTERS.map((f) => (
                <FilterChip
                  key={f.value}
                  label={tAlbumTypes(f.slug)}
                  isActive={f.value === '' ? filters.types.length === 0 : filters.types.includes(f.value)}
                  onClick={() => toggleType(f.value)}
                />
              ))}
            </div>
          </div>
        </div>
      </FilterSection>

      {/* Album Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
        {albums.map((album, index) => {
          const memberColor = getMemberColor(album.member);
          return (
            <button
              key={album.id}
              onClick={() => onSelectAlbum(index)}
              className="group text-left"
            >
              <div
                className="w-full aspect-square rounded-lg overflow-hidden mb-2 md:mb-3 border transition-all group-hover:border-white/30"
                style={{ borderColor: album.member ? `${memberColor}30` : 'rgba(255,255,255,0.08)' }}
              >
                <div
                  className="w-full h-full bg-[#1A1A1A] bg-cover bg-center group-hover:scale-105 transition-transform duration-500"
                  style={{ backgroundImage: `url(${album.coverUrl})` }}
                />
              </div>
              <div className="space-y-0.5 md:space-y-1">
                <p className="text-[10px] md:text-xs text-[#888888]">{album.year}</p>
                <p className="text-xs md:text-sm font-semibold text-[#F5F5F0] group-hover:text-white transition-colors line-clamp-1">
                  {album.title}
                </p>
                <p
                  className="text-[10px] md:text-xs flex items-center gap-1 md:gap-1.5"
                  style={{ color: memberColor }}
                >
                  <span
                    className="w-1 h-1 md:w-1.5 md:h-1.5 rounded-full"
                    style={{ backgroundColor: memberColor }}
                  />
                  {album.artist}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
